import { useState } from 'react';

import { Typography } from '@/components/ui/typography';
import { useReleaseFlag } from '@/hooks/useReleaseFlag';
import { isFreeTier, type FloorPlanTier } from './floorPlanOffer';
import { FloorPlanExampleDialog } from './FloorPlanExampleDialog';
import { FloorPlanTierCard } from './FloorPlanTierCard';

interface FloorPlanTierPanelProps {
  tiers: FloorPlanTier[];
}

/**
 * The floor plan tier choice shown under a selected package that comes with a
 * free floor plan (DV-3204). It sends nothing to the cart: order-service adds
 * the free Basic line when the order is created.
 */
export function FloorPlanTierPanel({ tiers }: FloorPlanTierPanelProps) {
  const [exampleTier, setExampleTier] = useState<FloorPlanTier | null>(null);
  const examplesReleased = useReleaseFlag('floorPlanExamples');

  return (
    <section
      aria-labelledby="floor-plan-tier-heading"
      className="space-y-4 rounded-2xl border border-primary bg-card p-4 shadow-card sm:p-5"
    >
      <Typography
        as="h3"
        id="floor-plan-tier-heading"
        variant="heading-md"
        className="text-lg leading-7"
      >
        Select Your Floor Plan
      </Typography>

      <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {tiers.map((tier) => (
          <li key={tier.id}>
            <FloorPlanTierCard
              tier={tier}
              // Basic is the only selection until the paid tiers launch.
              isSelected={isFreeTier(tier)}
              onShowExample={examplesReleased ? setExampleTier : undefined}
            />
          </li>
        ))}
      </ul>

      <FloorPlanExampleDialog
        tier={exampleTier}
        onClose={() => setExampleTier(null)}
      />
    </section>
  );
}
